import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, KeyRound, Loader2, Mail, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const field = "w-full rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none";

const TIER_OF = { department: "department", storefront: "storefront", kiosk: "kiosk", pad: "standalone" };

/**
 * You pointed at an empty unit on the board. This is the little card the leasing office slides
 * across the counter: which unit, what it costs, and two ways to take it further.
 */
export default function UnitPopup({ slot, onClose }) {
    const navigate = useNavigate();
    const [tiers, setTiers] = useState(null);
    const [asking, setAsking] = useState(false);
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", what_you_sell: "", message: "" });

    useEffect(() => { api.get("/stores/tiers").then(({ data }) => setTiers(data)).catch(() => setTiers({ tiers: [] })); }, []);
    useEffect(() => {
        const esc = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", esc);
        return () => window.removeEventListener("keydown", esc);
    }, [onClose]);

    const tierId = TIER_OF[slot.kind === "pad" ? "pad" : slot.tier] || "storefront";
    const tier = (tiers?.tiers || []).find((t) => t.id === tierId) || null;
    const unit = String(slot.unit || "");

    const send = async () => {
        if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email so the office can write back"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/stores/lease-inquiry", { ...form, unit, tier: tierId });
            toast.success(data.note);
            setSent(true);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="unit-popup">
            <div className="absolute inset-0 bg-black/55" onClick={onClose} />
            <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-black/10 bg-white shadow-2xl">
                <div className="flex items-start gap-3 border-b border-black/10 bg-zinc-100 px-5 py-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded border-2 border-dashed border-zinc-500 font-mono text-base font-black text-zinc-900"
                        data-testid="unit-popup-number">
                        {unit}
                    </span>
                    <div className="min-w-0">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-amber-700">
                            <KeyRound size={10} className="mr-1 inline" /> Open space · 13thav
                        </p>
                        <h3 className="font-display text-xl font-black tracking-tight text-zinc-900" data-testid="unit-popup-tier">
                            {slot.kind === "pad" ? "The pad site" : `Unit ${unit}`} · {tier?.name || "Space"}
                        </h3>
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700" data-testid="unit-popup-price">
                            {tier ? `$${tier.price_month}/mo${tier.price_year ? ` · $${tier.price_year.toLocaleString()}/yr` : ""}` : "—"} · 50% off month one
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="unit-popup-close"
                        className="ml-auto shrink-0 text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
                </div>

                <div className="px-5 py-4">
                    {!tiers ? (
                        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-400" /></div>
                    ) : sent ? (
                        <div className="py-4 text-center" data-testid="unit-popup-sent">
                            <Check size={26} className="mx-auto text-zinc-900" />
                            <p className="mt-3 text-sm text-zinc-900">
                                The office has your note about unit {unit}. Somebody writes back to {form.email}.
                            </p>
                        </div>
                    ) : asking ? (
                        <div data-testid="unit-popup-form">
                            <p className="mb-3 text-sm text-zinc-900">
                                Ask the office about <span className="font-semibold">unit {unit}</span> — no card, no commitment.
                            </p>
                            <div className="space-y-2.5">
                                <input className={field} placeholder="Your name" value={form.name} data-testid="unit-ask-name"
                                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                <input className={field} type="email" placeholder="Email" value={form.email} data-testid="unit-ask-email"
                                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                <input className={field} placeholder="Phone (optional)" value={form.phone} data-testid="unit-ask-phone"
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                                <input className={field} placeholder="What do you print or sell?" value={form.what_you_sell} data-testid="unit-ask-sells"
                                    onChange={(e) => setForm({ ...form, what_you_sell: e.target.value })} />
                                <textarea className={field} rows={3} placeholder="Anything you want to ask about the unit…"
                                    value={form.message} data-testid="unit-ask-message"
                                    onChange={(e) => setForm({ ...form, message: e.target.value })} />
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <button onClick={send} disabled={busy} data-testid="unit-ask-send"
                                    className="inline-flex items-center gap-2 rounded-full bg-zinc-800 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-50 hover:bg-black disabled:opacity-50">
                                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} send it to the office
                                </button>
                                <button onClick={() => setAsking(false)} data-testid="unit-ask-back"
                                    className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600 hover:text-zinc-900">back</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-zinc-900">{tier?.tagline}</p>
                            <ul className="mt-3 space-y-1.5">
                                {(tier?.perks || []).slice(0, 4).map((p) => (
                                    <li key={p} className="flex gap-2 text-sm text-zinc-800">
                                        <Check size={14} className="mt-0.5 shrink-0 text-zinc-500" /> {p}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-5 flex flex-wrap items-center gap-2">
                                <button data-testid="unit-lease-cta"
                                    onClick={() => navigate(`/account/sell?unit=${encodeURIComponent(unit)}&tier=${tierId}`)}
                                    className="inline-flex items-center gap-2 rounded-full bg-zinc-800 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-50 hover:bg-black">
                                    <KeyRound size={12} /> ask about this space
                                </button>
                                <button onClick={() => setAsking(true)} data-testid="unit-ask-cta"
                                    className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800 hover:border-zinc-900">
                                    <Mail size={12} /> ask the office about unit {unit}
                                </button>
                            </div>
                            <p className="mt-3 text-[11px] text-zinc-600">
                                Your first month is half price on any unit, and you can walk away whenever.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
